import React, { useCallback, useEffect, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';

import { ModalHeader } from '../../components/ModalHeader';
import { useModal } from '../../hooks/modal';
import { useToast } from '../../hooks/toast';
import { getCroppedImg } from './getCroppedImg';

import {
  Cancel,
  Container,
  Content,
  CropperWrapper,
  FooterRow,
  Submit,
  ZoomRow,
} from './styles';

interface CropImageModalProps {
  file: File;
  onConfirm: (file: File) => void;
}

const CropImageModal: React.FC<CropImageModalProps> = ({
  file,
  onConfirm,
}) => {
  const { removeModal } = useModal();
  const { addToast } = useToast();

  const [imageSrc] = useState(() => URL.createObjectURL(file));
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(
    null,
  );
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    return () => URL.revokeObjectURL(imageSrc);
  }, [imageSrc]);

  const handleCropComplete = useCallback((_: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!croppedAreaPixels) {
      return;
    }

    setProcessing(true);

    try {
      const croppedFile = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        file.name,
        file.type,
      );

      removeModal();
      onConfirm(croppedFile);
    } catch {
      setProcessing(false);
      addToast({
        type: 'error',
        title: 'Erro ao recortar imagem',
        description: 'Tente novamente ou selecione outro arquivo.',
      });
    }
  }, [croppedAreaPixels, imageSrc, file, onConfirm, removeModal, addToast]);

  return (
    <Container>
      <ModalHeader
        title="Ajustar imagem"
        subtitle="Arraste para posicionar e use o zoom para ajustar o recorte"
      />

      <Content>
        <CropperWrapper>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={handleCropComplete}
          />
        </CropperWrapper>

        <ZoomRow>
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={e => setZoom(Number(e.target.value))}
          />
        </ZoomRow>

        <FooterRow>
          <Cancel type="button" onClick={() => removeModal()}>
            Cancelar
          </Cancel>
          <Submit
            type="button"
            name="confirmCrop"
            value="Confirmar"
            loading={processing}
            disabled={processing || !croppedAreaPixels}
            onClick={handleConfirm}
          />
        </FooterRow>
      </Content>
    </Container>
  );
};

export { CropImageModal };
